import { useState } from 'react';

import MetaTags from '@otovo/shared/components/Sanity/MetaTags';
import getTitleFromSanityCampaignPage from '@otovo/shared/components/Sanity/getTitleFromSanityPage';
import { ADDRESS_SEARCH_FIELD_PLACEHOLDER } from '@otovo/shared/constants/otovoweb';
import {
  Sanity$CampaignPage,
  Sanity$Meta,
} from '@otovo/shared/types/sanityTypes';

import { SANITY_VARIANT_PAGE_SLUG_PREFIX } from '@otovo/shared/lib/posthog/constants';
import { stegaClean } from 'next-sanity';
import dynamic from 'next/dynamic';
import StructuredDataScripts from '../Sanity/StructuredDataScripts';
import ContentRowList from './ContentRowList';
import HeroSection from './components/HeroSection/HeroSection';

const LandingPageNotification = dynamic(
  () => import('../LandingPageNotification/LandingPageNotification'),
);
const SanityBanner = dynamic(
  () => import('../LandingPageNotification/SanityBanner'),
);

type Props = {
  page: Sanity$CampaignPage;
};

const SanityLandingPage = ({ page }: Props) => {
  const [showBanner, setShowBanner] = useState(!!page.banner?.content);

  const heroAddressBar = [];
  const contentRows = page.rows.filter((row, i) => {
    if (
      i === 0 &&
      'placeholder' in row &&
      stegaClean(row.placeholder) === ADDRESS_SEARCH_FIELD_PLACEHOLDER
    ) {
      heroAddressBar.push(row);
      return false;
    }
    return true;
  });

  const { faqItems = null, mainHardwareType } = page;
  const rowContext = {
    faqItems,
    mainHardwareType,
  };

  function getMeta(): Sanity$Meta {
    if (!page.meta) {
      return null;
    }
    if (page.slug?.current.startsWith(SANITY_VARIANT_PAGE_SLUG_PREFIX)) {
      return page.meta;
    }

    return {
      ...page.meta,
      canonicalURLOverride: null,
    };
  }
  const meta = getMeta();

  return (
    <>
      <LandingPageNotification>
        {page.banner?.content && (
          <SanityBanner
            banner={page.banner}
            onDismiss={() => setShowBanner(false)}
          />
        )}
      </LandingPageNotification>
      <MetaTags
        meta={meta}
        defaultTags={{
          title: getTitleFromSanityCampaignPage(page),
          description: page.lead || '',
          image: page.mainImage,
        }}
      />
      {meta?.structuredData && (
        <StructuredDataScripts structuredData={meta.structuredData} />
      )}
      <HeroSection
        page={page}
        addressBarRow={heroAddressBar}
        isBannerShown={showBanner}
      />
      <div>
        <ContentRowList rows={contentRows} rowContext={rowContext} />
      </div>
    </>
  );
};

export default SanityLandingPage;
